/**
 * Pure geometry for the table map editor: metre <-> px conversion, area shapes
 * (rectangle / ellipse / polygon), table footprint defaults, and the
 * point-in-area / nearest-outline-point math shared by every editor step.
 *
 * Kept free of React so the store layer (mappers.ts) can reuse the same
 * constants and types as the components.
 */

export interface Point {
    x: number;
    y: number;
}

export interface SizeMeters {
    width: number;
    height: number;
}

export type AreaShapeKind = 'Rectangle' | 'Ellipse' | 'Polygon';

/** offset/size are metres (bbox against the global rect's origin); points are 0..1 within the bbox, Polygon only. */
export interface EditorArea {
    kind: AreaShapeKind;
    offset: Point;
    size: SizeMeters;
    points: Point[] | null;
}

/** Metres at centimetre precision — every drag commits through this, matching the numeric fields' step. */
export function snapMeters(value: number): number {
    return Math.round(value * 100) / 100;
}

export function snapPointMeters(p: Point): Point {
    return { x: snapMeters(p.x), y: snapMeters(p.y) };
}

export type TableShape = 'round' | 'square' | 'rectangle';

export const TABLE_SHAPES: TableShape[] = ['round', 'square', 'rectangle'];

export type TableShapeSizes = Record<TableShape, SizeMeters>;

export const DEFAULT_MAP_SIZE: SizeMeters = { width: 12, height: 7 };

export const DEFAULT_AREA: EditorArea = {
    kind: 'Rectangle',
    offset: { x: 0, y: 0 },
    size: { width: 12, height: 7 },
    points: null,
};

/** Seed for a fresh draft's per-shape defaults, and the fallback when a map has none to seed from. */
export const TABLE_SHAPE_DEFAULTS: TableShapeSizes = {
    round: { width: 0.7, height: 0.7 },
    square: { width: 0.7, height: 0.7 },
    rectangle: { width: 1.4, height: 0.7 },
};

/**
 * TablesMapManager.MinMapTableSizeMeters / MaxMapTableSizeMeters. The backend also rejects a
 * dimension larger than the map on that axis.
 */
export const TABLE_SIZE_MIN = 0.1;
export const TABLE_SIZE_MAX = 10;

/** `0` on either axis means "legacy / unset" — fall back to the draft's per-shape default. */
export function tableFootprintMeters(
    shape: TableShape,
    width: number,
    height: number,
    defaults: TableShapeSizes
): SizeMeters {
    const fallback = defaults[shape];
    return { width: width || fallback.width, height: height || fallback.height };
}

export function tableFootprintPx(
    shape: TableShape,
    width: number,
    height: number,
    defaults: TableShapeSizes,
    room: RoomRect
): SizeMeters {
    const m = tableFootprintMeters(shape, width, height, defaults);
    return { width: m.width * room.pxPerMeter, height: m.height * room.pxPerMeter };
}

// Polygon presets — unit space (0..1) within the area's bbox. Shared verbatim
// with the james.rn.shared renderer so an L drawn here matches the phone.
export const POLYGON_PRESETS: Record<'L' | 'T' | 'U', Point[]> = {
    L: [
        { x: 0, y: 0 },
        { x: 0.6, y: 0 },
        { x: 0.6, y: 0.5 },
        { x: 1, y: 0.5 },
        { x: 1, y: 1 },
        { x: 0, y: 1 },
    ],
    T: [
        { x: 0, y: 0 },
        { x: 1, y: 0 },
        { x: 1, y: 0.4 },
        { x: 0.7, y: 0.4 },
        { x: 0.7, y: 1 },
        { x: 0.3, y: 1 },
        { x: 0.3, y: 0.4 },
        { x: 0, y: 0.4 },
    ],
    U: [
        { x: 0, y: 0 },
        { x: 0.3, y: 0 },
        { x: 0.3, y: 0.6 },
        { x: 0.7, y: 0.6 },
        { x: 0.7, y: 0 },
        { x: 1, y: 0 },
        { x: 1, y: 1 },
        { x: 0, y: 1 },
    ],
};

export type AreaPreset = 'rectangle' | 'square' | 'circle' | 'oval' | 'L' | 'T' | 'U';

function pointsEqual(a: Point[] | null, b: Point[]): boolean {
    if (!a || a.length !== b.length) return false;
    return a.every((p, i) => p.x === b[i].x && p.y === b[i].y);
}

export function detectPreset(area: EditorArea): AreaPreset | 'custom' {
    if (area.kind === 'Polygon') {
        if (pointsEqual(area.points, POLYGON_PRESETS.L)) return 'L';
        if (pointsEqual(area.points, POLYGON_PRESETS.T)) return 'T';
        if (pointsEqual(area.points, POLYGON_PRESETS.U)) return 'U';
        return 'custom';
    }
    const isSquareLike = area.size.width === area.size.height;
    if (area.kind === 'Rectangle') return isSquareLike ? 'square' : 'rectangle';
    return isSquareLike ? 'circle' : 'oval';
}

// ---- SVG canvas <-> metres ----

export const SVG_WIDTH = 800;
export const SVG_HEIGHT = 500;
// Margin (SVG user units) kept around the global rect so the entrance marker/label fits.
const CANVAS_MARGIN = 40;

export interface RoomRect {
    x: number;
    y: number;
    width: number;
    height: number;
    pxPerMeter: number;
}

/** One scale factor derived from the map's global size, aspect preserved and letterboxed in the canvas. */
export function computeRoom(size: SizeMeters): RoomRect {
    const availableW = SVG_WIDTH - CANVAS_MARGIN * 2;
    const availableH = SVG_HEIGHT - CANVAS_MARGIN * 2;
    const pxPerMeter = Math.min(availableW / size.width, availableH / size.height);
    const width = size.width * pxPerMeter;
    const height = size.height * pxPerMeter;
    return {
        x: (SVG_WIDTH - width) / 2,
        y: (SVG_HEIGHT - height) / 2,
        width,
        height,
        pxPerMeter,
    };
}

export interface AreaBBoxPx {
    x: number;
    y: number;
    width: number;
    height: number;
}

/** Area bbox in absolute SVG px. */
export function areaBBoxPx(area: EditorArea, room: RoomRect): AreaBBoxPx {
    return {
        x: room.x + area.offset.x * room.pxPerMeter,
        y: room.y + area.offset.y * room.pxPerMeter,
        width: area.size.width * room.pxPerMeter,
        height: area.size.height * room.pxPerMeter,
    };
}

/** Polygon points (unit space) mapped into absolute SVG px via the area bbox. */
export function polygonPointsPx(area: EditorArea, room: RoomRect): Point[] {
    const bbox = areaBBoxPx(area, room);
    return (area.points ?? []).map((p) => ({
        x: bbox.x + p.x * bbox.width,
        y: bbox.y + p.y * bbox.height,
    }));
}

export function clamp(v: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, v));
}

export function isPointInPolygon(p: Point, points: Point[]): boolean {
    let inside = false;
    for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
        const a = points[i];
        const b = points[j];
        const aAbove = a.y > p.y;
        const bAbove = b.y > p.y;
        const crossesRay = aAbove !== bAbove;
        const before = p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x;
        if (crossesRay && before) inside = !inside;
    }
    return inside;
}

function nearestPointOnSegment(p: Point, a: Point, b: Point): Point {
    const abx = b.x - a.x;
    const aby = b.y - a.y;
    const lenSq = abx * abx + aby * aby;
    const t = lenSq === 0 ? 0 : clamp(((p.x - a.x) * abx + (p.y - a.y) * aby) / lenSq, 0, 1);
    return { x: a.x + abx * t, y: a.y + aby * t };
}

function nearestPointOnPolygonUnit(p: Point, points: Point[]): Point {
    let best = points[0];
    let bestDist = Infinity;
    for (let i = 0; i < points.length; i++) {
        const a = points[i];
        const b = points[(i + 1) % points.length];
        const candidate = nearestPointOnSegment(p, a, b);
        const d = (candidate.x - p.x) ** 2 + (candidate.y - p.y) ** 2;
        if (d < bestDist) {
            bestDist = d;
            best = candidate;
        }
    }
    return best;
}

/** True when `p` (metres, against the global rect's origin) lies inside the area shape. */
export function isPointInArea(p: Point, area: EditorArea): boolean {
    const { offset, size } = area;
    if (area.kind === 'Rectangle') {
        return (
            p.x >= offset.x &&
            p.x <= offset.x + size.width &&
            p.y >= offset.y &&
            p.y <= offset.y + size.height
        );
    }
    if (area.kind === 'Ellipse') {
        const dx = (p.x - (offset.x + size.width / 2)) / (size.width / 2);
        const dy = (p.y - (offset.y + size.height / 2)) / (size.height / 2);
        return dx * dx + dy * dy <= 1;
    }
    const u = (p.x - offset.x) / size.width;
    const v = (p.y - offset.y) / size.height;
    return isPointInPolygon({ x: u, y: v }, area.points ?? []);
}

/** Clamp `p` (metres) to the nearest point inside — or on the boundary of — the area shape. */
export function clampPointToArea(p: Point, area: EditorArea): Point {
    if (isPointInArea(p, area)) return p;
    const { offset, size } = area;
    if (area.kind === 'Rectangle') {
        return {
            x: clamp(p.x, offset.x, offset.x + size.width),
            y: clamp(p.y, offset.y, offset.y + size.height),
        };
    }
    if (area.kind === 'Ellipse') {
        const cx = offset.x + size.width / 2;
        const cy = offset.y + size.height / 2;
        const rx = size.width / 2;
        const ry = size.height / 2;
        const dx = (p.x - cx) / rx;
        const dy = (p.y - cy) / ry;
        const dist = Math.hypot(dx, dy) || 1;
        return { x: cx + (dx / dist) * rx, y: cy + (dy / dist) * ry };
    }
    const u = (p.x - offset.x) / size.width;
    const v = (p.y - offset.y) / size.height;
    const nearest = nearestPointOnPolygonUnit({ x: u, y: v }, area.points ?? []);
    return { x: offset.x + nearest.x * size.width, y: offset.y + nearest.y * size.height };
}

export function clampCenterToArea(
    nx: number,
    ny: number,
    size: SizeMeters,
    area: EditorArea
): Point {
    const metres = { x: nx * size.width, y: ny * size.height };
    const clamped = snapPointMeters(clampPointToArea(metres, area));
    return { x: clamped.x / size.width, y: clamped.y / size.height };
}

/** Keep the area bbox at least MIN_BBOX_METERS per axis and fully inside `mapSize`. */
export const MIN_BBOX_METERS = 0.5;

export function clampAreaToMapSize(area: EditorArea, mapSize: SizeMeters): EditorArea {
    const width = clamp(area.size.width, MIN_BBOX_METERS, mapSize.width);
    const height = clamp(area.size.height, MIN_BBOX_METERS, mapSize.height);
    const offsetX = clamp(area.offset.x, 0, mapSize.width - width);
    const offsetY = clamp(area.offset.y, 0, mapSize.height - height);
    return { ...area, offset: { x: offsetX, y: offsetY }, size: { width, height } };
}

export interface OutlinePoint {
    point: Point; // metres, against the global rect's origin
    normal: Point; // unit vector, points outward
    tangent: Point; // unit vector, perpendicular to normal
    edge: { a: Point; b: Point } | null; // the straight edge containing `point` — null for Ellipse
}

export function unitPolygonToMetres(area: EditorArea): Point[] {
    return (area.points ?? []).map((p) => ({
        x: area.offset.x + p.x * area.size.width,
        y: area.offset.y + p.y * area.size.height,
    }));
}

/** Nearest point on the area's OUTLINE (not interior) to `p`, plus its outward normal/tangent. */
export function nearestPointOnAreaOutline(p: Point, area: EditorArea): OutlinePoint {
    const { offset, size } = area;
    if (area.kind === 'Rectangle') {
        const sides = [
            {
                a: { x: offset.x, y: offset.y },
                b: { x: offset.x + size.width, y: offset.y },
                normal: { x: 0, y: -1 },
            },
            {
                a: { x: offset.x, y: offset.y + size.height },
                b: { x: offset.x + size.width, y: offset.y + size.height },
                normal: { x: 0, y: 1 },
            },
            {
                a: { x: offset.x, y: offset.y },
                b: { x: offset.x, y: offset.y + size.height },
                normal: { x: -1, y: 0 },
            },
            {
                a: { x: offset.x + size.width, y: offset.y },
                b: { x: offset.x + size.width, y: offset.y + size.height },
                normal: { x: 1, y: 0 },
            },
        ];
        let best = sides[0];
        let bestPoint = nearestPointOnSegment(p, best.a, best.b);
        let bestDist = Infinity;
        for (const side of sides) {
            const candidate = nearestPointOnSegment(p, side.a, side.b);
            const d = (candidate.x - p.x) ** 2 + (candidate.y - p.y) ** 2;
            if (d < bestDist) {
                bestDist = d;
                best = side;
                bestPoint = candidate;
            }
        }
        return {
            point: bestPoint,
            normal: best.normal,
            tangent: { x: -best.normal.y, y: best.normal.x },
            edge: { a: best.a, b: best.b },
        };
    }
    if (area.kind === 'Ellipse') {
        const cx = offset.x + size.width / 2;
        const cy = offset.y + size.height / 2;
        const rx = size.width / 2;
        const ry = size.height / 2;
        const angle = Math.atan2((p.y - cy) / ry, (p.x - cx) / rx);
        const point = { x: cx + rx * Math.cos(angle), y: cy + ry * Math.sin(angle) };
        const nx = Math.cos(angle) / rx;
        const ny = Math.sin(angle) / ry;
        const len = Math.hypot(nx, ny) || 1;
        const normal = { x: nx / len, y: ny / len };
        return { point, normal, tangent: { x: -normal.y, y: normal.x }, edge: null };
    }

    const metresPoints = unitPolygonToMetres(area);
    const centroid = metresPoints.reduce(
        (acc, pt) => ({
            x: acc.x + pt.x / metresPoints.length,
            y: acc.y + pt.y / metresPoints.length,
        }),
        { x: 0, y: 0 }
    );
    let best = {
        point: metresPoints[0],
        normal: { x: 0, y: -1 },
        edge: { a: metresPoints[0], b: metresPoints[0] },
    };
    let bestDist = Infinity;
    for (let i = 0; i < metresPoints.length; i++) {
        const a = metresPoints[i];
        const b = metresPoints[(i + 1) % metresPoints.length];
        const candidate = nearestPointOnSegment(p, a, b);
        const d = (candidate.x - p.x) ** 2 + (candidate.y - p.y) ** 2;
        if (d < bestDist) {
            bestDist = d;
            const tx = b.x - a.x;
            const ty = b.y - a.y;
            const tLen = Math.hypot(tx, ty) || 1;
            let nx = ty / tLen;
            let ny = -tx / tLen;
            const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
            const toOutside = { x: mid.x - centroid.x, y: mid.y - centroid.y };
            if (nx * toOutside.x + ny * toOutside.y < 0) {
                nx = -nx;
                ny = -ny;
            }
            best = { point: candidate, normal: { x: nx, y: ny }, edge: { a, b } };
        }
    }
    return { ...best, tangent: { x: -best.normal.y, y: best.normal.x } };
}
